<template>
  <div class="test-page">
    <div class="test-header">
      <div class="header-content">
        <div class="back-button" @click="goBack">
          <i class="el-icon-arrow-left"></i>
          返回课程
        </div>
        <div class="test-title">
          <h1>{{ courseName }}测试</h1>
          <p>测试您对课程内容的掌握程度</p>
        </div>
        <div class="test-progress">
          <span>{{ currentPage }}/{{ totalPages }}</span>
        </div>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <i class="el-icon-loading"></i>
      <p>正在加载测试题目...</p>
    </div>

    <!-- 测试内容 -->
    <div v-else-if="currentQuestion" class="test-content">
      <div class="question-container">
        <div class="question-header">
          <div class="question-number">第{{ currentPage }}题</div>
          <div class="question-type">{{ questionTypeText }}</div>
        </div>
        
        <div class="question-content">
          <h3>{{ currentQuestion.topic }}</h3>
          
          <div class="options-container">
            <!-- 单选题：使用单选框 -->
            <el-radio-group 
              v-if="!isMultipleChoice && !isTextQuestion" 
              v-model="userAnswer" 
              class="options-list"
            >
              <el-radio 
                v-for="(option, index) in questionOptions" 
                :key="index"
                :label="option.value"
                class="option-item"
              >
                <span class="option-label">{{ option.label }}</span>
                <span class="option-text">{{ option.text }}</span>
              </el-radio>
            </el-radio-group>

            <!-- 多选题：使用复选框 -->
            <el-checkbox-group 
              v-if="isMultipleChoice" 
              v-model="userAnswer" 
              class="options-list"
            >
              <el-checkbox 
                v-for="(option, index) in questionOptions" 
                :key="index"
                :label="option.value"
                class="option-item"
              >
                <span class="option-label">{{ option.label }}</span>
                <span class="option-text">{{ option.text }}</span>
              </el-checkbox>
            </el-checkbox-group>

            <!-- 填空题和问答题：使用文本输入框 -->
            <el-input 
              v-if="isTextQuestion" 
              v-model="userAnswer" 
              type="textarea" 
              :rows="3" 
              placeholder="请输入答案" 
              class="text-input"
            ></el-input>
          </div>
        </div>
      </div>

      <!-- 操作按钮 -->
      <div class="test-actions">
        <el-button 
          v-if="currentPage > 1" 
          @click="previousQuestion"
          icon="el-icon-arrow-left"
        >
          上一题
        </el-button>
        
        <el-button 
          v-if="currentPage < totalPages" 
          type="primary" 
          @click="nextQuestion"
          :disabled="!isAnswerValid() || submittingAnswer"
          :loading="submittingAnswer"
          icon="el-icon-arrow-right"
        >
          {{ submittingAnswer ? '提交中...' : '下一题' }}
        </el-button>
        
        <el-button 
          v-if="currentPage === totalPages" 
          type="success" 
          @click="submitTest"
          :disabled="!isAnswerValid() || submittingAnswer"
          :loading="submittingAnswer"
          icon="el-icon-check"
        >
          {{ submittingAnswer ? '提交中...' : '提交测试' }}
        </el-button>
      </div>

      <!-- 进度条 -->
      <div class="progress-section">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: `${(currentPage / totalPages) * 100}%` }"
          ></div>
        </div>
        <div class="progress-text">
          已完成 {{ currentPage }} / {{ totalPages }} 题
        </div>
      </div>
    </div>

    <!-- 测试结果 -->
    <div v-else-if="showResult" class="test-result">
      <div class="result-container">
        <div class="result-icon">
          <i :class="resultIcon"></i>
        </div>
        <h2>{{ resultTitle }}</h2>
        <div class="score-display">
          <span class="score">{{ score }}</span>
          <span class="score-unit">分</span>
        </div>
        <p class="result-message">{{ resultMessage }}</p>
        
        <div class="result-actions">
          <el-button @click="goBack" icon="el-icon-back">
            返回课程
          </el-button>
          <el-button 
            v-if="score < 80" 
            type="primary" 
            @click="retakeTest"
            icon="el-icon-refresh"
          >
            重新测试
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from '@/common/request'

export default {
  name: 'Test',
  data() {
    return {
      categoryId: null,
      courseName: '',
      loading: true,
      currentQuestion: null, // 当前显示的题目
      currentPage: 1,
      totalPages: 0,
      userAnswers: {},
      userAnswer: '',
      showResult: false,
      score: 0,
      correctCount: 0,
      allQuestions: [], // 存储所有已加载的题目
      submittingAnswer: false // 答案提交状态
    }
  },
  
  computed: {
    questionTypeText() {
      if (!this.currentQuestion) return ''
      
      const typeMap = {
        0: '单选题',
        1: '多选题',
        2: '判断题',
        3: '填空题',
        4: '问答题'
      }
      
      return typeMap[this.currentQuestion.type] || '未知类型'
    },
    
    questionOptions() {
      if (!this.currentQuestion || !this.currentQuestion.option) return []
      
      // 根据题目类型处理选项
      if (this.currentQuestion.type === 0 || this.currentQuestion.type === 1) {
        // 单选题或多选题：解析选项字符串 "A:测试1;B:测试2;C:测试3;D:测试4"
        const optionString = this.currentQuestion.option
        
        // 先按分号分割，然后处理每个选项
        const options = optionString.split(';').map((opt, index) => {
          // 确保opt不是空字符串
          if (!opt.trim()) return null
          
          // 清理选项字符串，移除多余的空白字符
          const cleanOpt = opt.trim()
          
          // 查找第一个冒号的位置
          const colonIndex = cleanOpt.indexOf(':')
          
          if (colonIndex !== -1) {
            // 有冒号的情况：A: 倾听技巧
            const label = cleanOpt.substring(0, colonIndex).trim()
            const text = cleanOpt.substring(colonIndex + 1).trim()
            
            // 确保标签和文本都不为空
            if (label && text) {
              return {
                label: label,
                value: label,
                text: text
              }
            }
          } else {
            // 没有冒号的情况：整个字符串作为文本
            const text = cleanOpt
            const label = String.fromCharCode(65 + index) // A, B, C, D...
            
            return {
              label: label,
              value: label,
              text: text
            }
          }
          
          return null
        }).filter(opt => opt !== null) // 过滤掉空的选项
        
        console.log('解析后的选项:', options)
        return options
      } else if (this.currentQuestion.type === 2) {
        // 判断题：固定选项
        return [
          { label: 'A', value: 'A', text: '正确' },
          { label: 'B', value: 'B', text: '错误' }
        ]
      }
      
      return []
    },
    
    isMultipleChoice() {
      return this.currentQuestion && this.currentQuestion.type === 1
    },
    
    isTextQuestion() {
      return this.currentQuestion && (this.currentQuestion.type === 3 || this.currentQuestion.type === 4)
    },
    
    resultIcon() {
      return this.score >= 80 ? 'el-icon-success' : 'el-icon-error'
    },
    
    resultTitle() {
      return this.score >= 80 ? '恭喜通过测试！' : '测试未通过'
    },
    
    resultMessage() {
      if (this.score >= 80) {
        return `您已成功通过测试，得分${this.score}分。继续加油！`
      } else {
        return `很遗憾，您未能通过测试，得分${this.score}分。建议重新学习课程内容后再次测试。`
      }
    }
  },
  
  async created() {
    // 从路由参数获取课程信息
    this.categoryId = this.$route.query.categoryId
    this.courseName = this.$route.query.courseName || '课程'
    
    if (!this.categoryId) {
      this.$message.error('缺少课程分类ID参数')
      this.goBack()
      return
    }
    
    await this.loadQuestions()
  },
  
  methods: {
    async loadQuestions(page = 1) {
      try {
        this.loading = true
        
        // 调用测试题目接口，使用分类ID和分页参数
        const response = await get('/api.php/v1.course/lists', {
          cid: this.categoryId,
          page: page
        })
        
        if (response.data.code === 0) {
          const data = response.data.data
          
          // 更新分页信息
          this.currentPage = data.current_page || page
          this.totalPages = data.total || 0
          
          // 获取当前页的题目
          const pageQuestions = data.data || []
          
          if (pageQuestions.length > 0) {
            // 设置当前题目
            this.currentQuestion = pageQuestions[0] // 每页只有一道题
            
            // 如果还没有答案，初始化答案
            if (!this.userAnswers[this.currentQuestion.id]) {
              if (this.currentQuestion.type === 1) {
                // 多选题初始化为空数组
                this.userAnswers[this.currentQuestion.id] = []
              } else {
                this.userAnswers[this.currentQuestion.id] = ''
              }
            }
            
            // 设置当前答案
            this.userAnswer = this.userAnswers[this.currentQuestion.id] || ''
            
            // 将题目添加到总题目列表中（去重）
            pageQuestions.forEach(question => {
              const existingIndex = this.allQuestions.findIndex(q => q.id === question.id)
              if (existingIndex === -1) {
                this.allQuestions.push(question)
              } else {
                this.allQuestions[existingIndex] = question
              }
            })
          }
          
          console.log(`第${page}页题目加载成功:`, pageQuestions)
        } else {
          this.$message.error(response.data.msg || '获取测试题目失败')
        }
      } catch (error) {
        console.error('加载测试题目失败:', error)
        this.$message.error('网络错误，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    
    async nextQuestion() {
      if (!this.isAnswerValid()) {
        this.$message.warning('请先完成当前题目')
        return
      }
      
      // 保存当前答案
      if (this.currentQuestion) {
        this.userAnswers[this.currentQuestion.id] = this.userAnswer
      }
      
      // 调用答题接口提交答案
      await this.submitAnswer()
      
      if (this.currentPage < this.totalPages) {
        // 加载下一页题目
        await this.loadQuestions(this.currentPage + 1)
      }
    },
    
    // 提交答案到答题接口
    async submitAnswer() {
      if (!this.currentQuestion) return
      
      this.submittingAnswer = true
      
      try {
        const { post } = await import('@/common/request')
        
        // 根据题目类型处理答案格式
        let answerValue = this.userAnswer
        
        if (this.isMultipleChoice && Array.isArray(this.userAnswer)) {
          // 多选题：将数组转换为字符串，按照ABCD顺序排序，如 ["D", "C", "B", "A"] -> "ABCD"
          answerValue = this.userAnswer.sort().join('')
        }
        
        const answerData = {
          id: this.currentQuestion.tid,
          question_id: this.currentQuestion.question_id,
          paper_id: this.currentQuestion.paper_id,
          answer: answerValue
        }
        
        console.log('提交答案数据:', answerData)
        
        const response = await post('/api.php/v1.course/answer', answerData)
        
        if (response.data.code === 0) {
          console.log('答案提交成功:', {
            questionId: this.currentQuestion.id,
            answer: answerValue
          })
        } else {
          console.error('答案提交失败:', response.data.msg)
          // 不显示错误提示，避免影响用户体验
        }
      } catch (error) {
        console.error('提交答案时发生错误:', error)
        // 不显示错误提示，避免影响用户体验
      } finally {
        this.submittingAnswer = false
      }
    },
    
    async previousQuestion() {
      if (this.currentPage > 1) {
        // 加载上一页题目
        await this.loadQuestions(this.currentPage - 1)
      }
    },
    
    isAnswerValid() {
      if (this.isMultipleChoice) {
        // 多选题：至少选择一个选项
        return Array.isArray(this.userAnswer) && this.userAnswer.length > 0
      } else if (this.isTextQuestion) {
        // 填空题和问答题：不能为空
        return this.userAnswer && this.userAnswer.trim() !== ''
      } else {
        // 单选题和判断题：必须选择
        return this.userAnswer !== ''
      }
    },
    
    async submitTest() {
      if (!this.isAnswerValid()) {
        this.$message.warning('请先完成当前题目')
        return
      }
      
      this.submittingAnswer = true
      
      try {
      // 保存最后一题答案
      if (this.currentQuestion) {
        this.userAnswers[this.currentQuestion.id] = this.userAnswer
      }
      
      // 提交最后一题的答案
        await this.submitAnswer()
        
        // 调用提交测试接口
        const { post } = await import('@/common/request')
        const response = await post(`/api.php/v1.course/submit?id=${this.currentQuestion.paper_id}`)
        
        if (response.data.code === 0) {
          console.log('测试提交成功:', response.data.data)
          
          // 获取返回的成绩
          const score = response.data.data.total || 0
          const isPassed = score >= 80
          
          // 显示成绩弹窗
          this.$alert(
            `您的测试成绩为：${score}分\n\n${isPassed ? '合格' : '不合格'}`,
            '测试结果',
            {
              confirmButtonText: '确定',
              type: isPassed ? 'success' : 'error',
              center: true,
              customClass: 'test-result-dialog'
            }
          ).then(() => {
            // 跳转到根路径
            this.$router.push('/')
          })
        } else {
          this.$message.error(response.data.msg || '提交测试失败')
        }
      } catch (error) {
        console.error('提交测试时发生错误:', error)
        this.$message.error('网络错误，请稍后重试')
      } finally {
        this.submittingAnswer = false
      }
    },
    
    calculateScore() {
      // 现在分数从接口返回，这个方法主要用于本地计算（如果需要的话）
      let correct = 0
      const total = this.allQuestions.length
      
      this.allQuestions.forEach(question => {
        const userAnswer = this.userAnswers[question.id]
        
        if (question.type === 1) {
          // 多选题：答案必须完全匹配
          if (Array.isArray(userAnswer) && question.reference) {
            // 处理多选题答案，reference可能是字符串如"ABCD"或数组
            let correctAnswers = question.reference
            if (typeof correctAnswers === 'string') {
              // 将"ABCD"转换为["A", "B", "C", "D"]
              correctAnswers = correctAnswers.split('').map(ans => ans.trim())
            }
            
            const userSorted = [...userAnswer].sort()
            const refSorted = [...correctAnswers].sort()
            
            if (JSON.stringify(userSorted) === JSON.stringify(refSorted)) {
              correct++
            }
          }
        } else {
          // 其他类型：直接比较答案
          if (userAnswer === question.reference) {
            correct++
          }
        }
      })
      
      this.correctCount = correct
      this.score = Math.round((correct / total) * 100)
      
      console.log('本地计算结果:', {
        total,
        correct,
        score: this.score,
        userAnswers: this.userAnswers
      })
    },
    
    retakeTest() {
      this.currentPage = 1
      this.userAnswers = {}
      this.userAnswer = ''
      this.showResult = false
      this.score = 0
      this.correctCount = 0
      this.allQuestions = []
      this.loadQuestions(1)
    },
    
    goBack() {
      this.$confirm('中途退出考试，本次考试会占用一次机会，并且分数为0分，最多可以考三次。确定要退出吗？', '退出考试', {
        confirmButtonText: '确定退出',
        cancelButtonText: '继续考试',
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      }).then(() => {
        this.$router.go(-1)
      }).catch(() => {
        // 用户取消退出，继续考试
      })
    }
  }
}
</script>

<style scoped>
.test-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.test-header {
  background: linear-gradient(135deg, #2a91ff 0%, #667eea 100%);
  color: white;
  padding: 20px 0;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 8px;
  transition: background-color 0.3s;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.test-title {
  text-align: center;
  flex: 1;
}

.test-title h1 {
  margin: 0 0 8px 0;
  font-size: 2em;
}

.test-title p {
  margin: 0;
  opacity: 0.9;
  font-size: 1.1em;
}

.test-progress {
  font-size: 1.2em;
  font-weight: bold;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.loading-state {
  text-align: center;
  padding: 100px 20px;
  color: #666;
}

.loading-state i {
  font-size: 48px;
  color: #2a91ff;
  margin-bottom: 20px;
  display: block;
}

.test-content {
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
}

.question-container {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #f0f0f0;
}

.question-number {
  font-size: 1.2em;
  font-weight: bold;
  color: #2a91ff;
}

.question-type {
  background: #e6f0ff;
  color: #2a91ff;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9em;
}

.question-content h3 {
  font-size: 1.4em;
  color: #333;
  margin-bottom: 30px;
  line-height: 1.6;
}

.options-container {
  margin-top: 20px;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 12px;
}

.option-item:hover {
  border-color: #2a91ff;
  background: #f8f9ff;
}

.option-item.is-checked {
  border-color: #2a91ff;
  background: #e6f0ff;
}

.option-label {
  font-weight: bold;
  color: #2a91ff;
  margin-right: 16px;
  min-width: 30px;
}

.option-text {
  color: #333;
  font-size: 1.1em;
}

.text-input {
  margin-top: 20px;
}

.text-input .el-textarea__inner {
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 16px;
  font-size: 1.1em;
  transition: border-color 0.3s ease;
}

.text-input .el-textarea__inner:focus {
  border-color: #2a91ff;
}

.test-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.progress-section {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #2a91ff, #667eea);
  transition: width 0.3s ease;
}

.progress-text {
  text-align: center;
  color: #666;
  font-size: 1.1em;
}

.test-result {
  max-width: 600px;
  margin: 60px auto;
  padding: 0 20px;
}

.result-container {
  background: white;
  border-radius: 16px;
  padding: 60px 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.result-icon {
  font-size: 80px;
  margin-bottom: 30px;
}

.result-icon .el-icon-success {
  color: #52c41a;
}

.result-icon .el-icon-error {
  color: #f5222d;
}

.result-container h2 {
  font-size: 2em;
  color: #333;
  margin-bottom: 30px;
}

.score-display {
  margin-bottom: 30px;
}

.score {
  font-size: 4em;
  font-weight: bold;
  color: #2a91ff;
}

.score-unit {
  font-size: 1.5em;
  color: #666;
  margin-left: 8px;
}

.result-message {
  font-size: 1.2em;
  color: #666;
  line-height: 1.6;
  margin-bottom: 40px;
}

.result-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 20px;
  }
  
  .test-content {
    padding: 0 15px;
  }
  
  .question-container {
    padding: 30px 20px;
  }
  
  .test-actions {
    flex-direction: column;
    gap: 15px;
  }
  
  .result-actions {
    flex-direction: column;
    gap: 15px;
  }
}
</style> 